import { useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import {
    ItemRequest,
    createCampaignItem,
    extendCampaignItem,
    fetchCampaignItem,
    fetchSponsorTemplateById,
    updateCampaignItem,
} from 'api/campaigns';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useAuctionCategories } from 'components/pages/campaign-settings/campaign-auction/auction-categories/useAuctionCategories';
import {
    ItemFormData,
    ItemFormOptions,
    ItemType,
    ServerKey,
} from 'components/pages/campaign-settings/campaign-auction/items-form/types';
import { useCampaignItemRoutes } from 'components/pages/campaign-settings/campaign-auction/useCampaignItemRoutes';
import { UploadFile } from 'components/upload';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { dateFromServer, dateToServerDateTime, getMinDate } from 'helpers/dates';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { isImage } from 'helpers/regex';
import { camelCase } from 'lodash';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Auction, AuctionItem, CampaignItemType, MediaFile, SponsorItem } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const UPLOAD_COLLECTION = 'pictures';

const useItemForm = () => {
    const toast = useToast();
    const history = useHistory();
    const queryClient = useQueryClient();
    const {
        itemId: itemIdFromParams,
        templateId: sponsoredTemplateId,
        sponsorId,
    } = useParams<{ itemId: string; templateId: string; sponsorId: string }>();
    const itemId = parseInt(itemIdFromParams);
    const { data: campaign } = useCurrentCampaign();
    const { accountId } = useCurrentAccount();

    const itemRoutes = useCampaignItemRoutes();
    const campaignId = campaign?.id!;
    const auctionId = campaign?.auction_id!;
    const { data: categories } = useAuctionCategories({ campaignId, auctionId });
    const isDuplicating = useRouteMatch().path.includes('duplicate');

    const { data: auction } = useCurrentAuction();

    const { data: item } = useSuspenseQuery({
        queryKey: campaignQueryKeys.item(campaignId, itemId),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchCampaignItem<AuctionItem>({ campaignId, itemId: itemId }),
                !!itemId,
            ),
        select: data => {
            if (data) {
                return transformItemForDuplication(isDuplicating)(data);
            }

            return null;
        },
    });

    const { data: possibleSponsoredItem } = useSuspenseQuery({
        queryKey: campaignQueryKeys.sponsoredItem(campaignId, +sponsorId!, +sponsoredTemplateId!),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchSponsorTemplateById(accountId, +sponsorId, +sponsoredTemplateId),
                !!sponsoredTemplateId,
            ),
    });

    const [isDraft, setIsDraft] = useState(!item?.is_published);
    const mergedItem = mergeSponsorItemIntoAuctionItem(item, possibleSponsoredItem);

    const itemForm = toForm(auction, mergedItem as AuctionItem);
    const formMethods = useGivebutterForm<ItemFormData>({
        defaultValues: itemForm,
    });
    const { setError, getFieldState, getValues, resetFormButton } = formMethods;
    const [hasCustomBiddingDateRange, setHasCustomBiddingDateRange] = useState(
        !!item?.start_at_raw,
    );

    const isImageLoading =
        !!itemForm.pictures?.length &&
        !getValues('pictures').filter(f => f.hasOwnProperty('getMetadata')).length;
    const auctionStartDate = dateFromServer(auction.start_at);
    const customMinDate =
        auctionStartDate.toUTC() < getMinDate().toUTC() ? getMinDate() : auctionStartDate;

    const onError = (error: GivebutterAxiosError) => {
        setServerErrors(error);
        toast.error(error.response?.data.message ?? 'Unknown error occurred');
        resetFormButton();
    };

    const onSuccess = (itemId: number) => {
        queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.items(campaignId),
            type: 'all',
        });
        history.push(itemRoutes.show(itemId));
    };

    const {
        isPending: creatingItem,
        status: createItemStatus,
        mutate: createItem,
    } = useMutation({
        mutationFn: (formData: ItemFormData) => {
            const requestData = toRequest(
                auctionId,
                { ...formData, pictures: getValues('pictures') },
                mergedItem as AuctionItem,
                {
                    isDraft,
                    hasCustomBiddingDateRange,
                    arePhotosInitialized: !isImageLoading && getFieldState('pictures').isTouched,
                    extend: false,
                },
            );
            return createCampaignItem({ campaignId, data: requestData });
        },
        onError,
        onSuccess: ({ id }) => {
            toast.success('Item created!');
            onSuccess(id);
        },
    });

    const { isPending: updatingItem, mutate: updateItem } = useMutation({
        mutationFn: (formData: ItemFormData) => {
            const requestData = toRequest(
                auctionId,
                { ...formData, pictures: getValues('pictures') },
                mergedItem as AuctionItem,
                {
                    isDraft,
                    hasCustomBiddingDateRange,
                    arePhotosInitialized: !isImageLoading && getFieldState('pictures').isTouched,
                    extend: false,
                },
            );
            return updateCampaignItem({
                campaignId,
                itemId: item?.id!,
                data: requestData,
            });
        },
        onError,
        onSuccess: ({ id }) => {
            toast.success('Item saved!');
            onSuccess(id);
        },
    });

    const { isPending: extendingItem, mutate: updateAndExtend } = useMutation({
        mutationFn: async (formData: ItemFormData) => {
            const requestData = toRequest(
                auctionId,
                { ...formData, pictures: getValues('pictures') },
                mergedItem as AuctionItem,
                {
                    isDraft,
                    hasCustomBiddingDateRange,
                    arePhotosInitialized: !isImageLoading && getFieldState('pictures').isTouched,
                    extend: true,
                },
            );

            const itemId = item?.id || requestData.id;
            if (!itemId) throw new Error('Item ID is required');

            await extendCampaignItem({
                campaignId,
                itemId,
                end_at: requestData.end_at || auction.end_at,
            });

            return updateCampaignItem({
                campaignId,
                itemId,
                data: {
                    ...requestData,
                    end_at: hasCustomBiddingDateRange ? requestData.end_at : null,
                    is_published: true,
                },
            });
        },
        onError,
        onSuccess: ({ id }) => {
            toast.success('Item extended successfully!');
            onSuccess(id);
        },
    });

    const onSubmitItemForm = () => {
        const formData = getValues();
        const canExtend = item?.can_extend;

        let nextFn = createItem;

        if (item && !isDuplicating) {
            nextFn = canExtend ? updateAndExtend : updateItem;
        }

        return nextFn(formData);
    };

    const onDeleteItem = async () => {
        history.push(itemRoutes.index());
    };

    const transformServerFieldToFieldName = (serverKey: ServerKey): FieldPath<ItemFormData> => {
        switch (serverKey) {
            case 'price':
                return 'fixedPrice';
            case 'start_at':
            case 'end_at':
                return 'biddingDateRange';
            case 'total_quantity':
                return 'quantity';
            default:
                return camelCase(serverKey) as FieldPath<ItemFormData>;
        }
    };

    const setServerErrors = (error: GivebutterAxiosError) => {
        const errors = error.response?.data.errors;

        for (let i in errors) {
            setError(
                transformServerFieldToFieldName(i as ServerKey),
                {
                    type: 'custom',
                    message: errors[i][0],
                },
                {
                    shouldFocus: true,
                },
            );
        }
    };

    return {
        auction,
        item,
        campaignId,
        isLoading: creatingItem || updatingItem || extendingItem,
        isEditing: !!item,
        itemForm,
        hasCustomBiddingDateRange,
        itemRoutes,
        customMinDate,
        isDraft,
        setHasCustomBiddingDateRange,
        onSubmitItemForm,
        onDeleteItem,
        setIsDraft,
        formMethods,
        sponsorId,
        isDuplicating,
        showUnsavedItemPrompt: isDuplicating && createItemStatus === 'idle',
        categories,
        sponsoredItem: possibleSponsoredItem,
        updateAndExtend,
        isImageLoading,
    };
};

const computeBiddingDateRange = (item: AuctionItem, auction: Auction): [DateTime, DateTime] => {
    const minDateUTC = getMinDate().toUTC();
    const auctionStartUTC = dateFromServer(auction.start_at).toUTC();
    const formattedMinDate =
        auctionStartUTC < minDateUTC ? getMinDate() : dateFromServer(auction.start_at);

    const startAt = item?.start_at ? dateFromServer(item.start_at) : formattedMinDate;

    const endAt = item?.end_at ? dateFromServer(item.end_at) : dateFromServer(auction.end_at);

    return [startAt, endAt];
};

const toForm = (auction: Auction, item: AuctionItem): ItemFormData => {
    const itemAllowBidsLabel = item.allows_bid ? 'auction' : 'fixed';

    return {
        name: item?.name ?? '',
        description: item?.description ?? '',
        purchaseMessage: item?.purchase_message ?? '',
        visibility: item?.visibility ?? 'public',
        isPublished: item?.is_published ?? false,
        pictures: item?.pictures as unknown as UploadFile[],
        startBid: coerceNullNumber(item?.start_bid),
        bidIncrement: coerceNullNumber(item?.bid_increment),
        reservePrice: coerceNullNumber(item?.reserve_price),
        retailPrice: coerceNullNumber(item?.retail_price),
        shippingPrice: coerceNullNumber(item?.shipping_price),
        fixedPrice: coerceZeroToEmpty(item?.price),
        isShippingRequired: !!item?.require_shipping,
        popcornBidding: (item?.popcorn_add_seconds ?? 0) > 0,
        biddingDateRange: computeBiddingDateRange(item, auction),
        allowBids: item ? itemAllowBidsLabel : 'auction',
        quantity: item?.total_quantity?.toString() ?? '',
        allowsBuyNow: !!item.allows_buy_now,
        reservePriceMinimum: coerceNullNumber(item?.reserve_price_minimum),
        retailPriceMinimum: coerceNullNumber(item?.retail_price_minimum),
        media: item?.media as unknown as UploadFile[],
        hideSponsorMedia: !!item?.sponsor_item_config?.hide_media,
        hideSponsorDescription: !!item?.sponsor_item_config?.hide_description,
        hideSponsorPurchaseMessage: !!item?.sponsor_item_config?.hide_purchase_message,
        category_id: item?.category_id?.toString() ?? '',
        inPerson: item?.in_person ?? auction.in_person ?? false,
    };
};

const toRequest = (
    auctionId: number,
    item: ItemFormData,
    originalItem: AuctionItem,
    options: ItemFormOptions,
): ItemRequest => {
    const sponsorCanChangeName = originalItem?.sponsor_item?.config_data?.can_change_name ?? true;

    return {
        id: originalItem.id,
        auction_id: auctionId,
        description: item.description,
        purchase_message: item.purchaseMessage,
        visibility: item.visibility,
        is_published: !options.isDraft,
        type: 'product' as CampaignItemType,
        name: sponsorCanChangeName ? item.name : undefined,
        in_person: item.inPerson,
        ...getRequestPictures(item, options),
        ...getRequestPricingAndQuantity(item),
        ...getRequestBiddingFields(item),
        ...getRequestShippingFields(item),
        ...getRequestDateFields(item, options),
        ...getRequestSponsorItemFields(item, originalItem),
        ...getRequestCategoryFields(item),
    };
};

const getRequestPictures = (item: ItemFormData, { arePhotosInitialized }: ItemFormOptions) => ({
    pictures: arePhotosInitialized
        ? item.pictures
              .filter(f => f.hasOwnProperty('getMetadata'))
              .map(f => ({
                  id: f.getMetadata('id'),
                  url: f.getMetadata('url'),
                  size: f.fileSize,
                  mime_type: f.file.type,
                  collection: UPLOAD_COLLECTION,
              }))
        : [],
});

const getRequestPricingAndQuantity = (item: ItemFormData) => {
    const allowsBid = getAllowBids(item.allowBids);
    const fixedPrice = item.fixedPrice?.length ? unmaskNumber(item.fixedPrice) : 0;

    return {
        total_quantity: allowsBid ? 1 : unmaskNumber(item.quantity),
        allows_bid: allowsBid,
        price: item.allowsBuyNow ? fixedPrice : null,
        retail_price: item.retailPrice?.length ? unmaskNumber(item.retailPrice) : null,
    };
};

const getRequestBiddingFields = (item: ItemFormData) => ({
    reserve_price: item.reservePrice.length ? unmaskNumber(item.reservePrice) : null,
    start_bid: item.startBid.length ? unmaskNumber(item.startBid) : 0,
    bid_increment: item.bidIncrement.length ? unmaskNumber(item.bidIncrement) : 1,
    popcorn_add_seconds: item.popcornBidding ? 60 : 0,
});

const getRequestShippingFields = (item: ItemFormData) => ({
    shipping_price:
        item.isShippingRequired && item.shippingPrice.length ? unmaskNumber(item.shippingPrice) : 0,
    require_shipping: item.isShippingRequired,
});

const getRequestSponsorItemFields = (item: ItemFormData, originalItem: AuctionItem) => {
    if (!originalItem?.sponsor_item_id) {
        return {
            sponsor_item_id: null,
            sponsor_item_config: null,
        };
    }
    return {
        sponsor_item_id: originalItem.sponsor_item_id,
        sponsor_item_config: {
            hide_description: item.hideSponsorDescription,
            hide_media: item.hideSponsorMedia,
            hide_purchase_message: item.hideSponsorPurchaseMessage,
        },
    };
};

const getRequestCategoryFields = (item: ItemFormData) => ({
    category_id:
        item.category_id === 'NONE' || item.category_id === '' ? null : Number(item.category_id),
});

const getRequestDateFields = (
    item: ItemFormData,
    { hasCustomBiddingDateRange, extend }: ItemFormOptions,
) => ({
    start_at: hasCustomBiddingDateRange ? dateToServerDateTime(item.biddingDateRange[0]) : null,
    end_at:
        hasCustomBiddingDateRange || extend ? dateToServerDateTime(item.biddingDateRange[1]) : null,
});

const mergeSponsorItemIntoAuctionItem = (
    item: AuctionItem | null,
    sponsoredItem: SponsorItem | null,
): Partial<AuctionItem> => {
    const currentSponsoredItem = (item?.sponsor_item as SponsorItem) ?? sponsoredItem;
    const shippingPrice = item?.shipping_price ?? currentSponsoredItem?.shipping_price;
    const fixedPrice = item?.price ?? currentSponsoredItem?.price;
    const requireShipping = item?.require_shipping ?? currentSponsoredItem?.require_shipping;
    const allowsBuyNow = currentSponsoredItem?.allows_buy_now ?? true;
    const allowsBids = currentSponsoredItem?.allows_bid ?? item?.allows_bid ?? true;
    const sponsorInfo = currentSponsoredItem?.sponsor ?? null;
    const startAt = item?.start_at ?? currentSponsoredItem?.start_at;
    const endAt = item?.end_at ?? currentSponsoredItem?.end_at;

    const sponsoredMedia = currentSponsoredItem?.media?.filter((media: MediaFile) => {
        if (media?.url && isImage.exec(media.url)) {
            return media;
        }

        return false;
    });

    return {
        ...item,
        name: item?.name ?? currentSponsoredItem?.name,
        visibility: item?.visibility ?? currentSponsoredItem?.visibility ?? 'public',
        media: sponsoredMedia ?? null,
        start_bid: item?.start_bid ?? currentSponsoredItem?.start_bid,
        bid_increment: item?.bid_increment ?? currentSponsoredItem?.bid_increment,
        reserve_price: item?.reserve_price ?? currentSponsoredItem?.reserve_price ?? null,
        reserve_price_minimum:
            item?.reserve_price_minimum ?? currentSponsoredItem?.reserve_price_minimum,
        retail_price: item?.retail_price ?? currentSponsoredItem?.retail_price,
        retail_price_minimum: item?.retail_price ?? currentSponsoredItem?.retail_price,
        shipping_price: shippingPrice,
        price: fixedPrice ?? null,
        require_shipping: !!requireShipping,
        popcorn_add_seconds: item?.popcorn_add_seconds ?? currentSponsoredItem?.popcorn_add_seconds,
        allows_bid: !!allowsBids,
        allows_buy_now: !!allowsBuyNow,
        start_at: startAt ?? null,
        end_at: endAt ?? null,
        total_quantity: currentSponsoredItem?.total_quantity ?? item?.total_quantity,
        sponsor: sponsorInfo,
        sponsor_item_id: item?.sponsor_item_id ?? currentSponsoredItem?.id ?? null,
        sponsor_item: currentSponsoredItem as AuctionItem['sponsor_item'],
        sponsor_item_config: {
            hide_description: item?.sponsor_item_config?.hide_description
                ? item.sponsor_item_config.hide_description
                : false,
            hide_media: item?.sponsor_item_config?.hide_media
                ? item.sponsor_item_config.hide_media
                : false,
            hide_purchase_message: item?.sponsor_item_config?.hide_purchase_message
                ? item.sponsor_item_config.hide_purchase_message
                : false,
        },
    };
};

const transformItemForDuplication =
    (shouldDuplicate: boolean) =>
    (item: AuctionItem): AuctionItem => ({
        ...item,
        pictures: !shouldDuplicate
            ? item.pictures
            : (item.pictures?.map(p => ({ url: p.url })) as MediaFile[]),
        start_at: !shouldDuplicate ? item.start_at : null,
        end_at: !shouldDuplicate ? item.end_at : null,
        dashboard_status: !shouldDuplicate ? item.dashboard_status : 'draft',
    });

const coerceZeroToEmpty = (prop?: number | null) => {
    if (typeof prop === 'number' && prop !== 0) {
        return prop.toString();
    }

    return '';
};

const coerceNullNumber = (prop?: number | null) => {
    if (typeof prop === 'number') {
        return prop.toString();
    }

    return '';
};

const getAllowBids = (itemType: ItemType) => itemType === 'auction';

export { useItemForm, getAllowBids };
export type { ItemFormData };
